import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Clock, RefreshCw } from 'lucide-react';
import { PageLayout } from '@/components/shared/PageLayout';
import { PageHeader } from '@/components/shared/PageHeader';
import { EmptyState } from '@/components/shared/EmptyState';
import { useTranslation } from '@/hooks/useTranslation';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Callout } from '@/components/ui/callout';
import { Card } from '@/components/ui/card/Card';
import { IconButton } from '@/components/ui/icon-button';
import { Spinner } from '@/components/ui/spinner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ToggleChip } from '@/components/ui/toggle-chip';
import { useToolRecommender } from '../hooks/useToolRecommender';
import { useGaExperiments } from '../hooks/useGaExperiments';
import type { GaEvaluationInput } from '../types/ToolRecommenderTypes';
import type { BaseToolWire } from '../lib/baseToolSchema';
import { BaseToolSummary } from '../components/BaseToolSummary';
import { BaseToolSheet } from '../components/BaseToolSheet';

function formatElapsed(s: number): string {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

export function ToolRecommenderPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { status, result, error, elapsedSeconds, isResumed, launch, reset } = useToolRecommender();
  const { experiments, isLoading: expLoading, refresh: refreshHistory } = useGaExperiments();

  const [description, setDescription] = useState('');
  const [nJobs, setNJobs] = useState(32);
  const [topN, setTopN] = useState(5);
  const [baseTool, setBaseTool] = useState<BaseToolWire | null>(null);
  const [baseToolOpen, setBaseToolOpen] = useState(false);
  const [selectedMaterials, setSelectedMaterials] = useState<string[]>([]);
  const [selectedOperations, setSelectedOperations] = useState<string[]>([]);
  const formRef = useRef<HTMLDivElement>(null);

  const toggleChip = useCallback((item: string, setter: React.Dispatch<React.SetStateAction<string[]>>) => {
    setter((prev) => prev.includes(item) ? prev.filter((x) => x !== item) : [...prev, item]);
  }, []);

  const handleLaunch = useCallback(async () => {
    const input: GaEvaluationInput = {
      description: description || undefined,
      nJobs,
      topN,
      materials: selectedMaterials.length > 0 ? selectedMaterials : undefined,
      operations: selectedOperations.length > 0 ? selectedOperations : undefined,
      baseTool: baseTool ?? undefined,
    };
    await launch(input);
  }, [description, nJobs, topN, selectedMaterials, selectedOperations, baseTool, launch]);

  const MATERIALS = [
    'Acier <700 N/mm²', 'Acier >700 N/mm²', 'Acier Inoxydable', 'Acier trempé <58HRc',
    'Acier trempé <65HRc', 'Aluminium <8%Si', 'Aluminium >8%Si', 'Alliage Nickel',
    'Bronze', 'Céramique', 'Chrome-Cobalt/Phynox/Stellite', 'CuBe', 'Cuivre',
    'Fonte', 'Graphite', 'Inconel', 'Laiton', 'Laiton sans plomb',
    'Métal lourd (tungstène)', 'Polymère', 'Superalliages', 'Titane',
  ];
  const OPERATIONS = [
    'Surfaçage', 'Epaulement', 'Rainurage', 'Poche', 'Interpolation hélicoîdale',
  ];
  // The dataset spells this operation "hélicoîdale" (î) — the wire value must
  // match it exactly, so only the displayed label is corrected.
  const OPERATION_LABELS: Record<string, string> = {
    'Interpolation hélicoîdale': 'Interpolation hélicoïdale',
  };

  const breadcrumbItems = [
    { label: t('common.brand'), onClick: () => navigate('/') },
    { label: t('toolRecommender.page.breadcrumb') },
  ];

  // Navigate to the persistent detail page as soon as the experiment completes.
  useEffect(() => {
    if (status === 'completed' && result?.experimentId) {
      navigate(`/tool-recommender/experiments/${result.experimentId}`);
    }
  }, [status, result, navigate]);

  return (
    <PageLayout>
      <div className="flex flex-col gap-6">
        <PageHeader
          breadcrumb={breadcrumbItems}
          label={{ icon: Sparkles, text: t('toolRecommender.page.badge') }}
          title={t('toolRecommender.page.title')}
          description={t('toolRecommender.page.description')}
        />

        <Tabs defaultValue="generate">
          <TabsList>
            <TabsTrigger value="generate">{t('toolRecommender.tabs.generate')}</TabsTrigger>
            <TabsTrigger value="history">{t('toolRecommender.tabs.history')}</TabsTrigger>
          </TabsList>

          <TabsContent value="generate" className="mt-4">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start" ref={formRef}>
              {/* Config form */}
              <Card className="w-full lg:w-[340px] lg:shrink-0">
                <p className="eyebrow mb-4">
                  {t('toolRecommender.form.sectionTitle')}
                </p>
                <div className="flex flex-col gap-4">
                  <div>
                    <Label className="text-xs">{t('toolRecommender.form.descriptionLabel')}</Label>
                    <Input className="mt-1" value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t('toolRecommender.form.descriptionPlaceholder')} disabled={status === 'running'} />
                  </div>
                  <div>
                    <Label className="text-xs">{t('toolRecommender.form.materialsLabel')}</Label>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {MATERIALS.map((m) => (
                        <ToggleChip key={m} pressed={selectedMaterials.includes(m)} disabled={status === 'running'}
                          onClick={() => toggleChip(m, setSelectedMaterials)}>
                          {m}
                        </ToggleChip>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">{t('toolRecommender.form.operationsLabel')}</Label>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {OPERATIONS.map((o) => (
                        <ToggleChip key={o} pressed={selectedOperations.includes(o)} disabled={status === 'running'}
                          onClick={() => toggleChip(o, setSelectedOperations)}>
                          {OPERATION_LABELS[o] ?? o}
                        </ToggleChip>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">{t('toolRecommender.form.nJobsLabel')}</Label>
                      <Input className="mt-1 font-mono" type="number" min={1} value={nJobs} onChange={(e) => setNJobs(Number(e.target.value))} disabled={status === 'running'} />
                    </div>
                    <div>
                      <Label className="text-xs">{t('toolRecommender.form.topNLabel')}</Label>
                      <Input className="mt-1 font-mono" type="number" min={1} max={20} value={topN} onChange={(e) => setTopN(Number(e.target.value))} disabled={status === 'running'} />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">{t('toolRecommender.form.baseToolLabel')}</Label>
                    <BaseToolSummary
                      tool={baseTool}
                      onEdit={() => setBaseToolOpen(true)}
                      onRemove={() => setBaseTool(null)}
                      disabled={status === 'running'}
                    />
                  </div>
                  <Button className="w-full mt-1" onClick={() => void handleLaunch()} loading={status === 'running'}>
                    {status !== 'running' && <Sparkles aria-hidden />}
                    {t('toolRecommender.form.runButton')}
                  </Button>
                </div>
              </Card>

              {/* Results panel */}
              <div className="min-w-0 flex-1">
                {status === 'idle' && (
                  <EmptyState
                    icon={Sparkles}
                    title={t('toolRecommender.idle.title')}
                    description={t('toolRecommender.idle.description')}
                  />
                )}
                {status === 'running' && (
                  <Callout tone="info" loading title={t('toolRecommender.running.title')}>
                    <div className="flex items-center gap-4">
                      <p className="flex-1 text-xs text-muted-foreground">
                        {isResumed ? t('toolRecommender.running.resumedHint') : t('toolRecommender.running.hint')}
                      </p>
                      <span className="font-mono text-xl font-bold text-info tabular-nums shrink-0">
                        {formatElapsed(elapsedSeconds)}
                      </span>
                    </div>
                  </Callout>
                )}
                {status === 'timeout' && (
                  <Callout
                    tone="warning"
                    icon={Clock}
                    title={t('toolRecommender.timeout.title')}
                    action={
                      <Button variant="outline" size="sm" className="w-fit" onClick={reset}>{t('toolRecommender.form.runButton')}</Button>
                    }
                  >
                    <p>{t('toolRecommender.timeout.message')}</p>
                    <p className="text-xs text-muted-foreground">{t('toolRecommender.timeout.historyHint')}</p>
                  </Callout>
                )}
                {status === 'failed' && (
                  <Callout
                    tone="error"
                    title={t('toolRecommender.error.title')}
                    action={
                      <Button variant="outline" size="sm" className="w-fit" onClick={reset}>{t('toolRecommender.form.runButton')}</Button>
                    }
                  >
                    {error && <p className="text-xs text-muted-foreground">{t(error)}</p>}
                  </Callout>
                )}
                {status === 'completed' && result && (
                  <Callout tone="info" loading>
                    {t('toolRecommender.results.redirect')}
                  </Callout>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="history" className="mt-4">
            <Card padding="none" className="overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                <span className="text-sm font-semibold">{t('toolRecommender.history.sectionTitle')}</span>
                <IconButton
                  icon={RefreshCw}
                  label={t('toolRecommender.history.refresh')}
                  onClick={refreshHistory}
                  loading={expLoading}
                />
              </div>
              {expLoading ? (
                <div className="p-8 flex justify-center text-muted-foreground"><Spinner size="lg" label={t('common.loadingShort')} /></div>
              ) : !experiments || experiments.length === 0 ? (
                <EmptyState size="compact" title={t('toolRecommender.history.empty')} className="m-4" />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="eyebrow text-left px-4 py-3">{t('toolRecommender.history.colDate')}</th>
                        <th className="eyebrow text-left px-4 py-3">{t('toolRecommender.history.colDescription')}</th>
                        <th className="eyebrow text-left px-4 py-3">{t('toolRecommender.history.colId')}</th>
                        <th className="eyebrow text-left px-4 py-3">{t('toolRecommender.history.colResults')}</th>
                        <th className="px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody>
                      {experiments.map((exp) => (
                        <tr key={exp.experimentId} className="border-b border-border last:border-0 hover:bg-muted/40 transition-colors">
                          <td className="px-4 py-3 text-muted-foreground text-xs">{exp.createdAt ? new Date(exp.createdAt).toLocaleString() : '—'}</td>
                          <td className="px-4 py-3">{exp.description ?? '—'}</td>
                          <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{exp.experimentId.slice(0, 8)}…</td>
                          <td className="px-4 py-3">
                            {exp.resultsStored != null && (
                              <Badge>
                                {t('toolRecommender.history.results').replace('{{n}}', String(exp.resultsStored))}
                              </Badge>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Button type="button" variant="link" size="inline" onClick={() => navigate(`/tool-recommender/experiments/${exp.experimentId}`)}>
                              {t('toolRecommender.history.viewLink')} →
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      <BaseToolSheet
        open={baseToolOpen}
        onOpenChange={setBaseToolOpen}
        tool={baseTool}
        onApply={setBaseTool}
      />
    </PageLayout>
  );
}
